/**
 * Team, Analytics & AI APIs
 */
import type { ItemResponse, ListResponse, DeleteResponse } from '../../types/api.js';
import type { WorkspaceMember, Conversation, AISuggestion, Supplier, PurchaseOrder, StockUpdate } from '../../types/models.js';
import { apiRequest } from './core.js';

/** Approved WhatsApp template as the composer picker renders it. */
export interface InboxWaTemplate {
  name: string;
  language: string;
  category: string;
  bodyText: string;
  /** How many {{n}} values the body needs from the agent. */
  bodyParamCount: number;
  sendable: boolean;
  /** Why the composer cannot send this one yet — present when !sendable. */
  blockedReason?: string;
}

/** The message row returned by reply/send-template (thread append shape). */
export interface InboxSentMessage {
  id: string;
  sender: string;
  text: string;
  timestamp: string;
  deliveryStatus?: 'queued' | 'sent' | 'delivered' | 'read' | 'failed';
  deliveryError?: string;
  errorCode?: number;
}

export interface InboxCommentSync {
  workspaceId: string;
  ranAt: string;
  platforms: Array<{ platform: string; status: string; postsScanned: number; fetched: number; stored: number; detail?: string }>;
}

export const inboxApi = {
  getConversations: (params?: { limit?: number; offset?: number }) => {
    const searchParams = new URLSearchParams();
    if (params?.limit !== undefined) searchParams.set('limit', String(params.limit));
    if (params?.offset !== undefined) searchParams.set('offset', String(params.offset));
    const qs = searchParams.toString();
    return apiRequest<ListResponse<Conversation>>(`/inbox/conversations${qs ? `?${qs}` : ''}`);
  },
  getUnreadCount: () => apiRequest<ItemResponse<{ unread: number }>>('/inbox/conversations/unread-count'),
  /** Pull comments from the platforms without webhooks (YouTube, X, LinkedIn, TikTok); throttled server-side unless `force`. */
  syncComments: (opts?: { force?: boolean; socialPostId?: string }) => {
    const qs = new URLSearchParams();
    if (opts?.force) qs.set('force', 'true');
    if (opts?.socialPostId) qs.set('socialPostId', opts.socialPostId);
    const query = qs.toString();
    return apiRequest<ItemResponse<InboxCommentSync>>(
      `/inbox/sync-comments${query ? `?${query}` : ''}`,
      { method: 'POST' },
    );
  },
  getConversation: (id: string) => apiRequest<ItemResponse<Conversation>>(`/inbox/conversations/${id}`),
  sendReply: (id: string, text: string, replyTo?: { messageId: string; visibility: 'public' | 'private' }) =>
    apiRequest<ItemResponse<Conversation>>(`/inbox/conversations/${id}/reply`, { method: 'POST', body: JSON.stringify(replyTo ? { text, replyTo } : { text }) }),
  markRead: (id: string) => apiRequest<ItemResponse<{ read: boolean }>>(`/inbox/conversations/${id}/read`, { method: 'POST' }),
  closeConversation: (id: string) => apiRequest<ItemResponse<{ closed: boolean }>>(`/inbox/conversations/${id}/close`, { method: 'POST' }),
  getAISuggestion: (id: string) => apiRequest<ItemResponse<AISuggestion>>(`/inbox/conversations/${id}/ai-suggestion`),
  /** "Take over" — mute the bot on this conversation (human handoff). */
  pauseAutomation: (id: string) => apiRequest<ItemResponse<{ automationStatus: string }>>(`/inbox/conversations/${id}/pause-automation`, { method: 'POST' }),
  /** "Resume AI" — hand the conversation back to the bot. */
  resumeAutomation: (id: string) => apiRequest<ItemResponse<{ automationStatus: string }>>(`/inbox/conversations/${id}/resume-automation`, { method: 'POST' }),
  /** Approved WhatsApp templates — the reply mechanism when the 24h window is closed. */
  getTemplates: () => apiRequest<ItemResponse<{ templates: InboxWaTemplate[] }>>('/inbox/templates'),
  /** Send an approved template on a WhatsApp conversation (never window-gated). */
  sendTemplate: (id: string, body: { templateName: string; languageCode: string; bodyParams: string[] }) =>
    apiRequest<ItemResponse<InboxSentMessage>>(`/inbox/conversations/${id}/send-template`, { method: 'POST', body: JSON.stringify(body) }),
  /** The FIRST WhatsApp message to a customer: an approved template; opens the conversation when there is none. */
  startWhatsApp: (customerId: string, body: { templateName: string; languageCode: string; bodyParams: string[] }) =>
    apiRequest<ItemResponse<{ conversationId: string; message: InboxSentMessage }>>(
      `/inbox/customers/${customerId}/whatsapp-template`,
      { method: 'POST', body: JSON.stringify(body) },
    )
};

/** Public invite details returned by GET /team/invite/:token. */
export interface TeamInviteDetails {
  email: string | null;
  role: string;
  workspaceName: string;
  workspaceId: string;
  /** True when this address already has a BuzzBit account. */
  isExistingUser: boolean;
}

/**
 * The workspace that was joined, plus a JWT when the invite CREATED the
 * account. An address that already had an account must be signed in as itself
 * to accept, so no token comes back — the caller keeps its current session.
 */
export interface TeamInviteAcceptResult {
  token?: string;
  user: { id: string; email: string; name: string | null };
  workspace: { id: string; name: string };
}

export const teamApi = {
  getMembers: () => apiRequest<ListResponse<WorkspaceMember>>('/team/members'),
  inviteMember: (data: { email: string; role: 'ADMIN' | 'EDITOR' | 'VIEWER' }) =>
    apiRequest<ItemResponse<{ id: string; email: string; role: string; status: string; inviteLink: string }>>('/team/invite', { method: 'POST', body: JSON.stringify(data) }),
  removeMember: (id: string) => apiRequest<DeleteResponse>(`/team/members/${id}`, { method: 'DELETE' }),
  updateMemberRole: (id: string, role: 'ADMIN' | 'EDITOR' | 'VIEWER') =>
    apiRequest<ItemResponse<WorkspaceMember>>(`/team/members/${id}/role`, { method: 'PATCH', body: JSON.stringify({ role }) }),
  // Public (unauthenticated) invite endpoints — the emailed /invite/:token page.
  getInvite: (token: string) => apiRequest<ItemResponse<TeamInviteDetails>>(`/team/invite/${token}`),
  acceptInvite: (token: string, body: { password?: string; name?: string }) =>
    apiRequest<ItemResponse<TeamInviteAcceptResult>>(`/team/invite/${token}/accept`, { method: 'POST', body: JSON.stringify(body) }),
  resendInvite: (id: string) => apiRequest<ItemResponse<{ message: string }>>(`/team/invite/${id}/resend`, { method: 'POST' }),
  /**
   * Fetch one pending invite's link, on demand. The members list no longer
   * carries invite tokens — reading it used to hand over every outstanding
   * invite credential at once.
   */
  getInviteLink: (id: string) =>
    apiRequest<ItemResponse<{ link: string }>>(`/team/members/${id}/invite-link`)
};

export const procurementApi = {
  getSuppliers: () => apiRequest<ListResponse<Supplier>>('/procurement/suppliers'),
  createSupplier: (data: { name: string; email: string; phone?: string; leadTimeDays?: number; paymentTerms?: string }) =>
    apiRequest<ItemResponse<Supplier>>('/procurement/suppliers', { method: 'POST', body: JSON.stringify(data) }),
  updateSupplier: (id: string, data: Partial<Supplier>) => apiRequest<ItemResponse<Supplier>>(`/procurement/suppliers/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteSupplier: (id: string) => apiRequest<DeleteResponse>(`/procurement/suppliers/${id}`, { method: 'DELETE' }),
  getPurchaseOrders: () => apiRequest<ListResponse<PurchaseOrder>>('/procurement/purchase-orders'),
  createPurchaseOrder: (data: { supplierId: string; items: Array<{ productId: string; quantity: number; costPrice: number }>; expectedArrivalDate?: string; notes?: string }) =>
    apiRequest<ItemResponse<PurchaseOrder>>('/procurement/purchase-orders', { method: 'POST', body: JSON.stringify(data) }),
  sendPurchaseOrder: (id: string) =>
    apiRequest<ItemResponse<{ message: string }>>(`/procurement/purchase-orders/${id}/send`, { method: 'POST' }),
  receivePurchaseOrder: (id: string) =>
    apiRequest<ItemResponse<{ stockUpdates: StockUpdate[] }>>(`/procurement/purchase-orders/${id}/receive`, { method: 'POST' })
};
